/**
 * Web Mercator (EPSG:3857) cannot project ±90° — pigeon-maps lat2tile(-90) → Infinity,
 * which floods the console with invalid SVG path `d` attributes.
 */
export const MERCATOR_MAX_LAT = 85.05112878;

export function clampMercatorLat(lat: number): number {
  if (!Number.isFinite(lat)) return 0;
  return Math.max(-MERCATOR_MAX_LAT, Math.min(MERCATOR_MAX_LAT, lat));
}

function sanitizePosition(pos: unknown): number[] | null {
  if (!Array.isArray(pos) || pos.length < 2) return null;
  const lng = Number(pos[0]);
  const lat = Number(pos[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  if (lng < -180 || lng > 180) return null;
  return [lng, clampMercatorLat(lat), ...pos.slice(2).map(Number)];
}

function sanitizeRing(ring: unknown): number[][] | null {
  if (!Array.isArray(ring) || !ring.length) return null;
  const out: number[][] = [];
  for (const pos of ring) {
    const p = sanitizePosition(pos);
    if (p) out.push(p);
  }
  return out.length >= 2 ? out : null;
}

function sanitizePolygon(coords: unknown): number[][][] | null {
  if (!Array.isArray(coords)) return null;
  const rings: number[][][] = [];
  for (const ring of coords) {
    const r = sanitizeRing(ring);
    if (r) rings.push(r);
  }
  return rings.length ? rings : null;
}

function sanitizeGeometry(geometry: any): any | null {
  if (!geometry?.type || geometry.coordinates == null) return null;
  const { type, coordinates } = geometry;
  if (type === "Point") {
    const p = sanitizePosition(coordinates);
    return p ? { type, coordinates: p } : null;
  }
  if (type === "MultiPoint" || type === "LineString") {
    const ring = sanitizeRing(coordinates);
    return ring ? { type, coordinates: ring } : null;
  }
  if (type === "MultiLineString") {
    const lines: number[][][] = [];
    for (const line of coordinates || []) {
      const r = sanitizeRing(line);
      if (r) lines.push(r);
    }
    return lines.length ? { type, coordinates: lines } : null;
  }
  if (type === "Polygon") {
    const poly = sanitizePolygon(coordinates);
    return poly ? { type, coordinates: poly } : null;
  }
  if (type === "MultiPolygon") {
    const polys: number[][][][] = [];
    for (const poly of coordinates || []) {
      const p = sanitizePolygon(poly);
      if (p) polys.push(p);
    }
    return polys.length ? { type, coordinates: polys } : null;
  }
  if (type === "GeometryCollection") {
    const geoms = (geometry.geometries || [])
      .map(sanitizeGeometry)
      .filter(Boolean);
    return geoms.length ? { type, geometries: geoms } : null;
  }
  return geometry;
}

/** Drop / clamp GeoJSON so pigeon-maps never writes Infinity into SVG paths. */
export function sanitizeGeoJsonForMercator<T = any>(data: T): T | null {
  if (!data || typeof data !== "object") return null;
  const doc = data as any;
  if (doc.type === "FeatureCollection") {
    const features = (doc.features || [])
      .map((f: any) => {
        if (!f) return null;
        // Antarctica / polar outlines are useless on this map and force Infinity
        const slug = String(f.properties?.slug || "").toLowerCase();
        const countrySlug = String(f.properties?.countrySlug || "").toLowerCase();
        const regionSlug = String(f.properties?.regionSlug || "").toLowerCase();
        if (
          slug === "antarctica" ||
          slug === "aq" ||
          countrySlug === "antarctica" ||
          countrySlug === "aq" ||
          regionSlug === "antarctica"
        ) {
          return null;
        }
        const geometry = sanitizeGeometry(f.geometry);
        if (!geometry) return null;
        return { ...f, geometry };
      })
      .filter(Boolean);
    return { ...doc, features } as T;
  }
  if (doc.type === "Feature") {
    const geometry = sanitizeGeometry(doc.geometry);
    if (!geometry) return null;
    return { ...doc, geometry } as T;
  }
  const geometry = sanitizeGeometry(doc);
  return geometry as T;
}
